import { useState } from "react";
import { toast } from "sonner";
import { ClipboardCheck, Flag, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { SetTargetLevelForm } from "@/components/SetTargetLevelForm";
import PostSessionFeedbackModal from "@/components/PostSessionFeedbackModal";

const BTN = "text-[10px] uppercase tracking-wider px-2.5 py-1.5 border inline-flex items-center gap-1 disabled:opacity-50";

// Session roster: complete a player's initial assessment (level, then ratings + AI summary) without leaving the page.
export function SessionAssessmentActions({ p, sessionId, sessionTitle, sport, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(null);
  const [step, setStep] = useState(null); // null | "level" | "feedback"
  const [bookingId, setBookingId] = useState(p.booking_id);

  const flag = async () => {
    const { data } = await api.post(`/offerings/${sessionId}/flag-assessment`, { student_id: p.id });
    setBookingId(data.booking_id);
    return data.booking_id;
  };

  const markOnly = async () => {
    setBusy(true);
    try { await flag(); toast.success(`${p.name}'s booking is now their assessment session`); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't flag this player"); }
    finally { setBusy(false); }
  };

  const start = async () => {
    setBusy(true);
    try {
      if (!p.booking_is_assessment) {
        await flag();
        toast.info(`This booking is now ${p.name}'s assessment session`);
        onChanged?.();
      }
      const { data } = await api.get(`/coach/players/${p.id}/assessment-level`);
      setState(data);
      setStep("level");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open the assessment"); }
    finally { setBusy(false); }
  };

  const close = () => { setStep(null); onChanged?.(); };

  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-1.5" data-testid={`roster-assessment-${p.id}`}>
      <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 border border-[#FF9F1C]/60 text-[#FF9F1C]" data-testid={`roster-needs-assessment-${p.id}`}>
        Initial assessment needed
      </span>
      <button type="button" onClick={start} disabled={busy} data-testid={`roster-complete-assessment-${p.id}`}
        className={`${BTN} border-[#CCFF00] text-[#CCFF00] hover:bg-[#CCFF00] hover:text-black`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <ClipboardCheck className="w-3 h-3" />} Complete initial assessment
      </button>
      {!p.booking_is_assessment && (
        <button type="button" onClick={markOnly} disabled={busy} data-testid={`roster-flag-assessment-${p.id}`}
          className={`${BTN} border-[#404040] text-[#A3A3A3] hover:text-white hover:border-white`}>
          <Flag className="w-3 h-3" /> Make this their assessment session
        </button>
      )}
      <Dialog open={step === "level"} onOpenChange={(o) => { if (!o) close(); }}>
        <DialogContent className="bg-[#0F0F0F] border border-[#262626] max-w-2xl max-h-[90vh] overflow-y-auto text-[#E5E5E5]" data-testid="session-assessment-dialog">
          <DialogHeader>
            <DialogTitle>Initial assessment · {p.name}</DialogTitle>
            <DialogDescription className="text-sm text-[#A3A3A3] pt-1">Step 1 of 2: set the target and working level. Step 2: rate their skills — an AI summary is written on save.</DialogDescription>
          </DialogHeader>
          {state && (
            <SetTargetLevelForm studentId={p.id} playerName={p.name} al={state.assessment_level || null}
              workingLevel={state.working_level} highlight onSaved={() => setStep("feedback")} />
          )}
        </DialogContent>
      </Dialog>
      {step === "feedback" && (
        <PostSessionFeedbackModal open onClose={close} bookingId={bookingId}
          studentId={p.id} studentName={p.name} sessionTitle={sessionTitle} sport={sport} />
      )}
    </div>
  );
}
